// Data model, formatting and treemap layout for the /bids page.
//
// Every bid on the board is a funded offer from the offers API; collection
// floors and current listings only give each bid its reference price.

export interface BidCollection {
	slug: string
	name: string
	icon?: string | null
	icon_inscription?: string | null
	verified?: boolean
	floor_price?: number | null
	total_supply?: number | null
	listed?: number | null
}

export interface BidListing {
	inscription_id: string
	name?: string | null
	satoshi_price: number
}

export interface CollectionBid {
	id: string
	slug: string
	collection: BidCollection
	priceSats: number
	/** Sats of the collection floor at build time; null when unknown. */
	floorSats: number | null
	quantity: number
	bidder: string
	createdAt: number
}

export interface InscriptionBid {
	id: string
	slug: string
	collection: BidCollection
	inscriptionId: string
	name: string
	priceSats: number
	/** Current list price of the item, when it is listed. */
	listedSats: number | null
	bidder: string
	createdAt: number
}

export interface InscriptionBidGroup {
	slug: string
	collection: BidCollection
	totalSats: number
	bids: InscriptionBid[]
}

export type BidActivityKind = 'placed' | 'raised' | 'filled' | 'cancelled'

export interface BidActivity {
	id: string
	kind: BidActivityKind
	/** Copy that replaces the kind's default label, e.g. "Funding replaced" for a voided offer. */
	label?: string

	scope: 'collection' | 'inscription'
	slug: string
	collection: BidCollection
	inscriptionId?: string
	name: string
	priceSats: number
	bidder: string
	at: number
}

// ---------------------------------------------------------------------------
// Formatting

/**
 * ord.net-style amounts: BTC with up to five decimals from 0.001 BTC, whole
 * sats with separators below that.
 */
export function formatBidAmount(sats: number): { value: string; unit: 'btc' | 'sats' } {
	if (sats >= 100_000) {
		const btc = sats / 1e8
		return {
			value: btc.toLocaleString('en-US', { maximumFractionDigits: 5, minimumFractionDigits: 0 }),
			unit: 'btc'
		}
	}
	return { value: Math.round(sats).toLocaleString('en-US'), unit: 'sats' }
}

export function formatBtcTotal(sats: number): string {
	return (sats / 1e8).toLocaleString('en-US', {
		maximumFractionDigits: 5,
		minimumFractionDigits: 0
	})
}

export function percentOfFloor(
	bidSats: number,
	floorSats: number | null | undefined
): number | null {
	if (!floorSats || floorSats <= 0) return null
	return Math.round((bidSats / floorSats) * 100)
}

// ---------------------------------------------------------------------------
// Squarified treemap (Bruls, Huizing & van Wijk). Returns absolute rectangles
// in the same order as the input values.

export interface TreemapRect {
	index: number
	x: number
	y: number
	w: number
	h: number
}

function worstRatio(row: number[], side: number): number {
	const sum = row.reduce((a, b) => a + b, 0)
	if (sum <= 0 || side <= 0) return Infinity
	const max = Math.max(...row)
	const min = Math.min(...row)
	const s2 = side * side
	const sum2 = sum * sum
	return Math.max((s2 * max) / sum2, sum2 / (s2 * min))
}

export function squarify(values: number[], width: number, height: number): TreemapRect[] {
	const rects: TreemapRect[] = new Array(values.length)
	if (!values.length || width <= 0 || height <= 0) return []
	const total = values.reduce((a, v) => a + Math.max(0, v), 0)
	if (total <= 0) return []
	const scale = (width * height) / total
	const items = values
		.map((v, index) => ({ index, area: Math.max(0, v) * scale }))
		.filter((i) => i.area > 0)
		.sort((a, b) => b.area - a.area)

	let x = 0
	let y = 0
	let w = width
	let h = height
	let row: typeof items = []

	const layoutRow = (current: typeof items) => {
		const sum = current.reduce((a, i) => a + i.area, 0)
		if (w >= h) {
			// Column along the left edge.
			const colW = sum / h
			let cy = y
			for (const item of current) {
				const ih = item.area / colW
				rects[item.index] = { index: item.index, x, y: cy, w: colW, h: ih }
				cy += ih
			}
			x += colW
			w -= colW
		} else {
			// Row along the top edge.
			const rowH = sum / w
			let cx = x
			for (const item of current) {
				const iw = item.area / rowH
				rects[item.index] = { index: item.index, x: cx, y, w: iw, h: rowH }
				cx += iw
			}
			y += rowH
			h -= rowH
		}
	}

	for (const item of items) {
		const side = Math.min(w, h)
		const withItem = [...row, item]
		if (
			row.length === 0 ||
			worstRatio(
				withItem.map((i) => i.area),
				side
			) <=
				worstRatio(
					row.map((i) => i.area),
					side
				)
		) {
			row = withItem
		} else {
			layoutRow(row)
			row = [item]
		}
	}
	if (row.length) layoutRow(row)
	return rects.filter(Boolean)
}

/**
 * Tile weight is book depth — quantity × price — not the headline bid, so a
 * collection with ten bids near floor outranks one whale. Compressed hard so
 * the long tail stays legible while the ordering stays exact.
 */
export function tileWeight(priceSats: number, quantity = 1): number {
	return Math.pow(Math.max(priceSats * Math.max(quantity, 1), 1), 0.45)
}

export interface MinTileLayout {
	rects: TreemapRect[]
	/** Items that did not fit at the minimum size; drawn as one "+N" tile. */
	overflow: number
	overflowRect: TreemapRect | null
}

/**
 * Squarify, but never below a minimum tile size: trailing items are folded
 * into one overflow tile until every remaining tile clears the floor. Input
 * values must be sorted descending.
 */
export function squarifyWithMinimum(
	values: number[],
	width: number,
	height: number,
	minW: number,
	minH: number
): MinTileLayout {
	const empty = { rects: [], overflow: 0, overflowRect: null }
	if (!values.length || width <= 0 || height <= 0) return empty
	for (let n = values.length; n >= 1; n--) {
		const rest = values.slice(n)
		// The overflow tile is a door, not data: it takes the last kept tile's
		// weight rather than the (much larger) sum of what it hides.
		const input = rest.length ? [...values.slice(0, n), values[n - 1]] : values
		const rects = squarify(input, width, height)
		const own = rects.filter((r) => r.index < n)
		const fits = own.every((r) => r.w >= minW && r.h >= minH)
		if (fits || n === 1) {
			return {
				rects: own,
				overflow: rest.length,
				overflowRect: rects.find((r) => r.index === n) ?? null
			}
		}
	}
	return empty
}

/**
 * Colour carries meaning: a bid's label bar warms from neutral toward the OW
 * purple as it approaches the floor. Returns a CSS colour.
 */
export function floorTint(pct: number | null): string {
	if (pct == null) return 'rgb(0 0 0 / 0.72)'
	const t = Math.min(1, Math.max(0, (pct - 70) / 28))
	// Neutral black/72 → primary-700-ish purple/85.
	const r = Math.round(0 + t * 105)
	const g = Math.round(0 + t * 65)
	const b = Math.round(0 + t * 198)
	return `rgb(${r} ${g} ${b} / ${0.72 + t * 0.13})`
}

/** Treemap height for a given width and tile count, like ord.net's growing canvas. */
export function treemapHeight(
	width: number,
	count: number,
	{ min = 360, max = 1600 } = {}
): number {
	if (width <= 0) return min
	const ratio = count > 60 ? 1.05 : count > 30 ? 0.85 : count > 12 ? 0.7 : 0.55
	return Math.round(Math.min(max, Math.max(min, width * ratio)))
}
